import { useCallback, useEffect, useState } from "react";
import { GetBillingLinkAuthorization } from "./Services/UpfrontPaymentService";

type BillingAuthorizationState =
  | "checking"
  | "authorized"
  | "dob"
  | "denied"
  | "error";

interface UseBillingLinkAuthorizationParams {
  billingPK: string;
  accessToken?: string;
}

export const useBillingLinkAuthorization = ({
  billingPK,
  accessToken,
}: UseBillingLinkAuthorizationParams) => {
  const [authorizationState, setAuthorizationState] =
    useState<BillingAuthorizationState>("checking");

  const getAuthorizationState = useCallback(
    async (signal?: AbortSignal): Promise<BillingAuthorizationState> => {
      try {
        const authorization = await GetBillingLinkAuthorization(
          billingPK,
          accessToken,
          signal,
        );
        if (authorization.authorized) return "authorized";
        return authorization.requires_dob ? "dob" : "denied";
      } catch {
        return "error";
      }
    },
    [accessToken, billingPK],
  );

  const checkAuthorization = useCallback(
    async (signal?: AbortSignal) => {
      setAuthorizationState("checking");
      const nextState = await getAuthorizationState(signal);
      if (!signal?.aborted) {
        setAuthorizationState(nextState);
      }
    },
    [getAuthorizationState],
  );

  const recoverFromUnauthorizedResponse = useCallback(
    async (signal?: AbortSignal) => {
      const nextState = await getAuthorizationState(signal);
      if (!signal?.aborted) {
        setAuthorizationState(nextState === "authorized" ? "error" : nextState);
      }
    },
    [getAuthorizationState],
  );

  const retryAuthorization = useCallback(() => {
    checkAuthorization();
  }, [checkAuthorization]);

  useEffect(() => {
    const controller = new AbortController();
    checkAuthorization(controller.signal);
    return () => controller.abort();
  }, [checkAuthorization]);

  const markAuthorized = useCallback(() => {
    setAuthorizationState("authorized");
  }, []);

  const markAuthorizationError = useCallback(() => {
    setAuthorizationState("error");
  }, []);

  return {
    authorizationState,
    markAuthorized,
    markAuthorizationError,
    retryAuthorization,
    recoverFromUnauthorizedResponse,
  };
};
